import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zEncerramentoDeAssinatura, type AssinaturaResumida } from '@arena/shared/schemas';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { dataCompleta } from '../alunos/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { cobrancasApi, planosApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Botao, Campo, Mini, Nota, Status, Tabela, Tag, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  competenciaPorExtenso,
  haFaturaParaOBar,
  rotuloDaFatura,
  rotuloDaForma,
  SITUACAO_DA_FATURA,
  temBar,
  textoDoConsumoPendente,
} from './formatos.js';
import { ModalDeAssinatura } from './ModalDeAssinatura.jsx';
import { ModalDePagamento } from './ModalDePagamento.jsx';

/**
 * A aba Financeiro de `#m-aluno` — vazia desde o documento 08, "até o
 * documento 12 chegar".
 *
 * A mensalidade, o que foi pago no ano e o dia do vencimento; as faturas,
 * da mais recente para trás, com o "Receber" de quem registra pagamento; e
 * as assinaturas, com assinar e encerrar para quem aplica planos. As
 * faturas vêm pelo escopo de cobranças, as assinaturas pelo de planos — e
 * só para quem lê planos.
 *
 * A leitura fica na trilha: é dado pessoal, e o servidor registra com o id
 * da pessoa.
 */
export function AbaFinanceiroDoAluno({
  alunoId,
  alunoNome,
}: {
  alunoId: string;
  alunoNome: string;
}) {
  const { pode } = useSessao();
  const leCobrancas = pode('cobrancas', 'ler');
  const consulta = useQuery({
    queryKey: ['cobrancas', 'aluno', alunoId],
    queryFn: () => cobrancasApi.doAluno(alunoId),
    enabled: leCobrancas,
    retry: false,
  });
  const [faturaAberta, setFaturaAberta] = useState<string | null>(null);
  const [assinando, setAssinando] = useState(false);

  if (!leCobrancas) return <Vazio>Seu perfil não acessa cobranças.</Vazio>;
  if (consulta.isPending) return <EsqueletoDeCard linhas={4} />;
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Erro
        tipo={
          erro instanceof ErroDeRede
            ? 'rede'
            : erro instanceof ErroDeApi && erro.ehPermissao
              ? 'permissao'
              : 'falha'
        }
        detalhe={mensagemDoErro(erro)}
        onTentarDeNovo={() => void consulta.refetch()}
      />
    );
  }

  const dados = consulta.data;
  return (
    <>
      <Mini
        itens={[
          {
            valor: dados.mensalidade === null ? '—' : dinheiro(dados.mensalidade),
            rotulo: 'mensalidade',
          },
          { valor: dinheiro(dados.pagoNoAno), rotulo: `pago em ${dados.ano}` },
          {
            valor: dados.diaVencimento === null ? '—' : `Dia ${dados.diaVencimento}`,
            rotulo: 'vencimento',
          },
        ]}
      />

      {dados.consumoPendente.quantidade > 0 && (
        <Nota>
          <b>Consumo do bar a faturar: {dinheiro(dados.consumoPendente.total)}</b> (
          {dados.consumoPendente.quantidade}{' '}
          {dados.consumoPendente.quantidade === 1 ? 'comanda' : 'comandas'})
          {textoDoConsumoPendente(dados.consumoPendente, haFaturaParaOBar(dados.faturas)) && (
            <>
              <br />
              {textoDoConsumoPendente(dados.consumoPendente, haFaturaParaOBar(dados.faturas))}
            </>
          )}
        </Nota>
      )}

      {dados.faturas.length === 0 ? (
        <Vazio>Nenhuma fatura ainda.</Vazio>
      ) : (
        <Tabela colunas={['Competência', 'Vencimento', 'Pagamento', 'Valor', 'Forma', 'Situação']}>
          {dados.faturas.map((f) => (
            <tr
              key={f.id}
              className="clickable"
              style={{ cursor: 'pointer' }}
              onClick={() => setFaturaAberta(f.id)}
            >
              <td>{competenciaPorExtenso(f.competencia)}</td>
              <td>{dataCurta(f.vencimento)}</td>
              {/* A data do PAGAMENTO, que não é a do vencimento: é o que
                  mostra quem paga sempre depois do dia 5. */}
              <td>
                {f.pagoEm ? (
                  <>
                    {dataCurta(f.pagoEm)}
                    {f.diasDeAtraso > 0 && (
                      <small style={{ color: 'var(--dim)' }}> · +{f.diasDeAtraso}d</small>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--dim)' }}>—</span>
                )}
              </td>
              <td className="num">
                {dinheiro(f.valorPago ?? f.valor)}
                {temBar(f) && (
                  <>
                    {' '}
                    <Tag>com bar</Tag>
                  </>
                )}
              </td>
              <td>{rotuloDaForma(f.formaPagamento)}</td>
              <td>
                <Status situacao={SITUACAO_DA_FATURA[f.situacao]}>
                  {rotuloDaFatura(f)}
                  {f.encargosRenunciados !== null ? ' · com renúncia' : ''}
                </Status>
              </td>
            </tr>
          ))}
        </Tabela>
      )}

      {pode('planos', 'ler') && (
        <div>
          <div className="card-head" style={{ marginBottom: 10 }}>
            <h2>Assinaturas</h2>
            {pode('planos', 'aplicar') && (
              <Botao variante="contorno" pequeno onClick={() => setAssinando(true)}>
                Assinar plano
              </Botao>
            )}
          </div>
          {dados.assinaturas.length === 0 ? (
            <Vazio>Sem plano por enquanto.</Vazio>
          ) : (
            <ul className="feed">
              {dados.assinaturas.map((a) => (
                <LinhaDaAssinatura key={a.id} assinatura={a} hoje={dados.hoje} alunoId={alunoId} />
              ))}
            </ul>
          )}
        </div>
      )}

      {faturaAberta && (
        <ModalDePagamento faturaId={faturaAberta} onFechar={() => setFaturaAberta(null)} />
      )}
      {assinando && (
        <ModalDeAssinatura
          alunoId={alunoId}
          alunoNome={alunoNome}
          onFechar={() => setAssinando(false)}
        />
      )}
    </>
  );
}

function LinhaDaAssinatura({
  assinatura,
  hoje,
  alunoId,
}: {
  assinatura: AssinaturaResumida;
  hoje: string;
  alunoId: string;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const [encerrando, setEncerrando] = useState(false);
  const [fim, setFim] = useState(hoje);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const encerrar = useMutation({
    mutationFn: () =>
      planosApi.encerrarAssinatura(assinatura.id, zEncerramentoDeAssinatura.parse({ fim, motivo })),
    onSuccess: () =>
      Promise.all([
        cliente.invalidateQueries({ queryKey: ['cobrancas', 'aluno', alunoId] }),
        cliente.invalidateQueries({ queryKey: ['planos'] }),
      ]),
  });

  async function confirmar() {
    setErro(null);
    const pedido = zEncerramentoDeAssinatura.safeParse({ fim, motivo });
    if (!pedido.success) {
      setErro(pedido.error.issues[0]?.message ?? 'Confira o encerramento.');
      return;
    }
    try {
      const resultado = await encerrar.mutateAsync();
      const partes = ['Assinatura encerrada.'];
      if (resultado.faturasEmAbertoDepoisDoFim > 0) {
        partes.push(
          `${resultado.faturasEmAbertoDepoisDoFim} fatura(s) em aberto depois do fim — cancele se não forem devidas.`,
        );
      }
      if (resultado.consumoPendente.quantidade > 0) {
        partes.push(
          `Há ${dinheiro(resultado.consumoPendente.total)} de bar pendente e nenhuma fatura vai recebê-lo: cobre de outro jeito.`,
        );
      }
      avisar(partes.join(' '), resultado.consumoPendente.quantidade > 0 ? 'aviso' : undefined);
      setEncerrando(false);
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  const outros = assinatura.alunos.filter((p) => p.id !== alunoId);
  return (
    <li style={{ flexWrap: 'wrap' }}>
      <div>
        <b>
          {assinatura.plano.nome}
          {assinatura.valor !== null ? ` · ${dinheiro(assinatura.valor)}` : ''}
        </b>
        <small>
          desde {dataCompleta(assinatura.inicio)} · vence dia {assinatura.diaVencimento}
          {assinatura.fim ? ` · até ${dataCompleta(assinatura.fim)}` : ''}
          {assinatura.proximoValor
            ? ` · ${dinheiro(assinatura.proximoValor.valor)} a partir de ${dataCurta(assinatura.proximoValor.aPartirDe)}`
            : ''}
          {outros.length > 0 ? ` · com ${outros.map((o) => o.nome).join(', ')}` : ''}
        </small>
      </div>
      <div className="rt">
        <Status situacao={assinatura.ativa ? 'ok' : 'neutra'}>
          {assinatura.ativa ? 'Ativa' : assinatura.inicio > hoje ? 'Começa depois' : 'Encerrada'}
        </Status>
        {pode('planos', 'aplicar') && !assinatura.encerradaEm && !encerrando && (
          <button type="button" className="link" onClick={() => setEncerrando(true)}>
            Encerrar
          </button>
        )}
      </div>
      {encerrando && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            width: '100%',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <Campo rotulo="Fim — o primeiro dia sem contrato">
            <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
          </Campo>
          <Campo rotulo="Motivo" erro={erro ?? undefined}>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </Campo>
          <Botao variante="fantasma" pequeno onClick={() => setEncerrando(false)}>
            Voltar
          </Botao>
          <Botao pequeno onClick={() => void confirmar()} disabled={encerrar.isPending}>
            {encerrar.isPending ? 'Encerrando…' : 'Encerrar'}
          </Botao>
        </div>
      )}
    </li>
  );
}
